// ──────────────────────────────────────────────────────────────────────────
// subscription.js — client-side state for the freemium paywall.
//
// Three concerns, one module:
//
//   1. Entitlements cache. The user's effective tier (free / pro / lifetime)
//      and current period live in a module-level cache. Components subscribe
//      via useEntitlements() and re-render when the cache changes (sign-in,
//      RevenueCat purchase callback, webhook-driven refresh, sign-out).
//
//   2. Paywall queue. `presentPaywall()` is the single entry point any
//      feature uses to show the upgrade sheet. Mirrors aiConsent.js: a
//      global PaywallSheet mounted in App.jsx subscribes to the queue and
//      renders when something is pending.
//
//   3. 402 interceptor. `handleApiResponse(res, json)` returns true if the
//      response was a paywall block (HTTP 402 with our standard payload),
//      after enqueuing the paywall sheet. Callers that hit a paid API
//      should funnel responses through here so the sheet is consistent.
//
// The actual purchase flow (StoreKit / RevenueCat SDK calls) lives in
// purchases.js — this module is concerned with state + UI plumbing only.
// ──────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useSyncExternalStore } from 'react';
import { supabase } from './supabase.js';

// ── Entitlements cache ───────────────────────────────────────────────────
// `null` means "not loaded yet". `{ tier: 'free', ... }` means loaded.
// We keep the parsed object stable across getSnapshot calls (same rule as
// aiConsent.js) so useSyncExternalStore doesn't loop.

let cachedEntitlements = null;
let loadingPromise = null;

const FREE_DEFAULT = Object.freeze({
  tier: 'free',
  expiresAt: null,
  productId: null,
  period: null,
  willRenew: false,
});

export function getEntitlements() {
  return cachedEntitlements || FREE_DEFAULT;
}

// Fetch the user's profile row and cache the subscription columns.
// Idempotent: concurrent callers share one in-flight promise.
export function refreshEntitlements() {
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setEntitlements(FREE_DEFAULT);
        return FREE_DEFAULT;
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_expires_at, subscription_product_id, subscription_period, subscription_will_renew')
        .eq('id', user.id)
        .maybeSingle();
      if (error || !data) {
        setEntitlements(FREE_DEFAULT);
        return FREE_DEFAULT;
      }
      // Treat expired pro as free even before RC fires EXPIRATION — same
      // rule as the server's getEffectiveTier so client and server agree.
      let tier = data.subscription_tier || 'free';
      if (tier === 'pro') {
        const exp = data.subscription_expires_at ? new Date(data.subscription_expires_at) : null;
        if (!exp || exp <= new Date()) tier = 'free';
      }
      const value = Object.freeze({
        tier,
        expiresAt: data.subscription_expires_at || null,
        productId: data.subscription_product_id || null,
        period:    data.subscription_period    || null,
        willRenew: data.subscription_will_renew !== false,
      });
      setEntitlements(value);
      return value;
    } catch (err) {
      console.error('[subscription] refresh failed', err);
      setEntitlements(FREE_DEFAULT);
      return FREE_DEFAULT;
    } finally {
      loadingPromise = null;
    }
  })();

  return loadingPromise;
}

function setEntitlements(value) {
  cachedEntitlements = value;
  emitEntitlements();
}

// Clear cache on sign-out so the next refresh starts clean.
export function clearEntitlements() {
  cachedEntitlements = null;
  emitEntitlements();
}

const entitlementsListeners = new Set();
export function subscribeEntitlements(fn) {
  entitlementsListeners.add(fn);
  return () => entitlementsListeners.delete(fn);
}
function emitEntitlements() {
  for (const fn of entitlementsListeners) {
    try { fn(); } catch {}
  }
}

// Convenience accessors — these are what feature code calls when it wants
// to know "is this user entitled to X right now?" Avoid scattering string
// comparisons against 'free' all over the app.
export function isPro() {
  const t = getEntitlements().tier;
  return t === 'pro' || t === 'lifetime';
}
export function isFree() {
  return getEntitlements().tier === 'free';
}

// ── Paywall queue ────────────────────────────────────────────────────────
// Same shape as aiConsent's modal queue. The global PaywallSheet
// subscribes to `subscribePaywall` and reads `getPendingPaywall()` to
// decide what to render.

let pendingPaywall = null;        // { reason, usage } | null
const paywallListeners = new Set();

export function subscribePaywall(fn) {
  paywallListeners.add(fn);
  return () => paywallListeners.delete(fn);
}
function emitPaywall() {
  for (const fn of paywallListeners) {
    try { fn(); } catch {}
  }
}

export function getPendingPaywall() {
  return pendingPaywall;
}

// Show the paywall.
//
// `reason` is a short tag the sheet can use to customize copy:
//   'brief_limit' | 'tts_limit' | 'stt_limit' | 'llm_limit' | 'theme' | 'voice'
// `usage` is the optional 402 payload from the server (used to render
// "you've used 3 of 3 briefs this week"). Either may be omitted.
export function presentPaywall({ reason = null, usage = null } = {}) {
  pendingPaywall = { reason, usage, at: Date.now() };
  emitPaywall();
}

export function dismissPaywall() {
  pendingPaywall = null;
  emitPaywall();
}

// ── 402 interceptor ──────────────────────────────────────────────────────
// Helper for any caller that hits a metered endpoint (briefApi, llm,
// stt, tts). Pass the fetch Response and the parsed JSON; if it was a
// paywall block we enqueue the sheet and return true. The caller should
// then bail out of its normal success path.
//
//   const res = await fetch(...);
//   const json = await res.json().catch(() => null);
//   if (handleApiResponse(res, json, 'brief_limit')) return null;
//   if (!res.ok) throw new Error(json?.error || 'failed');
//
// We also surface the headers TTS responses use, since that endpoint
// returns binary MP3 not JSON. handleTtsResponse below covers that case.

const KIND_TO_REASON = {
  brief: 'brief_limit',
  llm:   'llm_limit',
  stt:   'stt_limit',
  tts:   'tts_limit',
};

export function handleApiResponse(res, json, fallbackReason = null) {
  if (res.status !== 402) return false;
  const usage = json?.usage || null;
  const reason = (usage && KIND_TO_REASON[usage.kind]) || fallbackReason;
  presentPaywall({ reason, usage });
  return true;
}

// TTS sends headers, not JSON, on success. On 402 it does send JSON; this
// helper handles both cases. Returns true if the response was a paywall.
export async function handleTtsResponse(res) {
  if (res.status !== 402) return false;
  const json = await res.json().catch(() => null);
  return handleApiResponse(res, json, 'tts_limit');
}

// ── Lifecycle hooks ──────────────────────────────────────────────────────
// Wire entitlement refresh into the auth lifecycle. Called from useAuth.

export async function onSignIn() {
  await refreshEntitlements();
}

export function onSignOut() {
  clearEntitlements();
  pendingPaywall = null;
  emitPaywall();
}

// Cross-tab sync: when another tab refreshes entitlements (e.g. RevenueCat
// callback from a purchase), bump our cache too. Keyed off a sentinel
// localStorage write rather than the actual values, which we wouldn't
// trust from another origin anyway.
const REFRESH_SIGNAL_KEY = 'proxera.subscription.refresh';
export function broadcastRefresh() {
  try { localStorage.setItem(REFRESH_SIGNAL_KEY, String(Date.now())); } catch {}
}
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (e) => {
    if (e.key === REFRESH_SIGNAL_KEY) {
      refreshEntitlements();
    }
  });
}

// ── React hooks ──────────────────────────────────────────────────────────
// useSyncExternalStore-based subscriptions. Components that need to
// re-render on entitlement / paywall changes use these.

export function useEntitlements() {
  return useSyncExternalStore(subscribeEntitlements, getEntitlements, getEntitlements);
}

export function useIsPro() {
  const e = useEntitlements();
  return e.tier === 'pro' || e.tier === 'lifetime';
}

export function useIsFree() {
  return useEntitlements().tier === 'free';
}

// Returns the current paywall request (or null) and re-renders when one
// is enqueued / dismissed. The global PaywallSheet uses this.
export function usePaywall() {
  return useSyncExternalStore(subscribePaywall, getPendingPaywall, getPendingPaywall);
}

// ── Usage counters (RLS-readable) ────────────────────────────────────────
// usage_events has a `select_own` RLS policy, so the client can count its
// own events directly without going through a server endpoint. Returns
// { brief7d, llm24h, stt24h, tts24h, loading } and refreshes whenever the
// caller passes a new `key` (e.g. after a brief generation completes).

export function useUsageCounts(key = 0) {
  const [state, setState] = useState({ brief7d: 0, llm24h: 0, stt24h: 0, tts24h: 0, loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          if (!cancelled) setState(s => ({ ...s, loading: false }));
          return;
        }
        const cutoff7d  = new Date(Date.now() - 7  * 24 * 60 * 60 * 1000).toISOString();
        const cutoff24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        // Run the four counts in parallel.
        const countOf = async (kind, cutoff) => {
          const { count } = await supabase
            .from('usage_events')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('kind', kind)
            .gt('created_at', cutoff);
          return count || 0;
        };
        const [brief7d, llm24h, stt24h, tts24h] = await Promise.all([
          countOf('brief', cutoff7d),
          countOf('llm',   cutoff24h),
          countOf('stt',   cutoff24h),
          countOf('tts',   cutoff24h),
        ]);
        if (!cancelled) setState({ brief7d, llm24h, stt24h, tts24h, loading: false });
      } catch (err) {
        console.error('[subscription] useUsageCounts failed', err);
        if (!cancelled) setState(s => ({ ...s, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [key]);

  return state;
}
